import { generatePath } from 'react-router';
import { RxSocketUrls } from '@Constants/CommonConstants';
import { config as CONSTANTS } from '@Constants/rx-ui/constants';
import { EXPORT_EVENTS } from '@Services/ExportService/ExportConstants';
import { PROFILE_ERRORS_LIST } from '@Pages/ProfileSettings/ProfileSettingsConstants';
import Search from '@Services/Search';
import NavigationHistory from '@Services/NavigationHistory';
import some from 'lodash/some';
import get from 'lodash/get';
import includes from 'lodash/includes';
import isString from 'lodash/isString';
import isObject from 'lodash/isObject';
import isUndefined from 'lodash/isUndefined';
import extend from 'lodash/extend';
import isEmpty from 'lodash/isEmpty';
import capitalize from 'lodash/capitalize';
import { loginLimitErrorType } from '@Pages/LoginLimit/LoginLimitConstants';
import { dispatchOpenUnavailableResultsDialog, validateSocketError as isResultNameSocketError } from '@Containers/ResultSetValidation';
import { unavailableResultsMessage } from '@Containers/ResultSetValidation/ResultSetValidationConstants';
import AppStorage from '@Services/AppStorage';
import { replaceLocation, getLocationHash } from '@Utils/navigation';
import Catalyst from '@Services/Catalyst';
import RXService from '@Services/RxService';
import { generateTraceHeaders } from '@Services/CorrelationId';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import { typeOfUnavailableResultsDialog } from '@Containers/ResultSetValidation/UnavailableResultsDialog';
import SocketBroker from '@Services/SocketBroker';
import RxSocket from '@Services/RxSocket';
import Auth from '@Services/Auth';
import RequestUrls from '@Constants/RequestUrlsConstants';
import { paths } from '@AppRoutes/constants';
import { getTrackingPathName } from '@AppRoutes/AppRoutesSelectors';
import history from '@AppRoutes/history';
import store from '@Store';

const HTML_CONTENT_TYPES = ['text/html', 'application/xhtml+xml', 'multipart/form-data'];
const idleModalUrl = 'common/rx-user/views/idle-warning.html';
const rxModalUrl = 'common/rx-ui/views/rx-modal/rx-modal.html';

// eslint-disable-next-line import/no-mutable-exports
export let errorModalActive = false;

// eslint-disable-next-line no-shadow
function _handleActiveLoginsLimit(response) {
  history.push({
    pathname: paths.LOGIN_LIMIT,
    state: {
      message: get(response, 'data.message'),
    },
  });
}

function _handleUnavailableResults() {
  dispatchOpenUnavailableResultsDialog();
  Auth.goToLandingPage(true);
}

function _isNoReload(stateCurrentName) {
  return stateCurrentName.indexOf('public.register') >= 0 || stateCurrentName.indexOf('public.login') >= 0;
}

// eslint-disable-next-line no-shadow
function _handleLoggedInNavigation(response, stateCurrentName) {
  const configData = get(response, 'config.data', '');
  if (isString(configData) && configData.indexOf('accessToken') >= 0) {
    return; // session upgrade with failed password
  }

  const isInPublicOrRegisterState = stateCurrentName.indexOf('public.') >= 0 || stateCurrentName.indexOf('register') >= 0;

  if (!isInPublicOrRegisterState) {
    Auth.cleanUp();
  } else if (stateCurrentName.indexOf('public.redirect') >= 0 && response.status === 401) {
    Auth.setRememberMeToken();
  }
  replaceLocation('/');
}

function _redirectToLoginPage(content, stateCurrentName) {
  if (_isNoReload(stateCurrentName)) {
    return;
  }

  // revisit last state if it is whitelisted
  const lastVisitedState = NavigationHistory.getLastVisitedStateForLogin();
  if (lastVisitedState) {
    AppStorage.instance.set('revisitLastState', true);
  }

  history.push({
    pathname: generatePath(paths.LOGIN),
    state: {
      loginFail: Auth.isGrantAnonymoususer() ? null : content?.text,
    },
  });
}

// eslint-disable-next-line no-shadow
export function _mainToPublic(response, content) {
  const userData = AppStorage.instance.get('userInstitutionInfo');
  const isRememberChoice = userData && userData.rememberChoice;
  // eslint-disable-next-line no-param-reassign
  content.isClean = true;
  const stateCurrentName = getTrackingPathName(store.getState()) || '';

  if (Auth.isLoggedIn()) {
    _handleLoggedInNavigation(response, stateCurrentName);
  } else if (isRememberChoice) {
    history.push({
      pathname: paths.LOGIN_INSTITUTION,
    });
  } else {
    _redirectToLoginPage(content, stateCurrentName);
  }
}

// eslint-disable-next-line no-shadow
export function _showGenericErrorModal(response, isGoToLogin, modalProps = {}) {
  const { level, title, msg, link } = modalProps;
  const url = get(response, 'config.url', '');
  const type = get(response, 'data.type');
  const { status } = response;

  const XMLorAEIsDown = url.indexOf('/oauth/token') >= 0 && (type === 'EXTERNAL' || type === 'ERROR_RETRY');
  const BEIsDown = status === 404 || status === 504;
  const isErrorHealthcheck = url.indexOf('healthcheck?forwardedChecks=true&xmlAndAeChecksOnly=true') > 0;
  if (isGoToLogin) {
    // eslint-disable-next-line no-use-before-define
    if (getErrorLevel(response) === 'fatal') {
      Auth.logoutOnExpiredSession(null, msg, false, true);
    } else if (XMLorAEIsDown || BEIsDown || isErrorHealthcheck) {
      return;
    } else {
      _mainToPublic(response, { text: msg });
    }
    return;
  }

  if (errorModalActive) {
    return;
  }

  errorModalActive = true;

  NotifyDialogsService.openModalWindowPromise({
    type: level,
    confirmText: 'Continue',
    traceparent: get(response, 'data.traceparent', '') || get(response, 'config.headers.traceparent', ''),
    timestamp: get(response, 'data.timestamp', new Date().toString()),
    title,
    text: msg,
    link,
  })
    .then(() => {
      errorModalActive = false;
    })
    .catch(Logger.error);
}

// eslint-disable-next-line no-shadow
export function trackResponseError(response, error) {
  Catalyst.trackError(
    {
      ...response,
      ...error,
      statusCode: response.status,
      errorNumber: response.errorNumber || get(response, 'data.errorNumber') || error.errorNumber,
      component: response.component || get(response, 'data.component') || error.component,
    },
    'se-'
  );
}

// eslint-disable-next-line no-shadow
function getErrorLevel(response) {
  return get(response, 'data.level', 'error')?.toLowerCase();
}

// eslint-disable-next-line no-shadow
function _handleDataChoice(response) {
  history.push({
    pathname: paths.LOGIN_ORGANIZATION,
    state: {
      response,
    },
  });
}

// eslint-disable-next-line no-shadow
export function getGenericCDNErrorMessage(response, defaultTitle) {
  let title = defaultTitle;
  let msg =
    'Something goes wrong\n' +
    '\n' +
    'Please retry your last action in a few minutes. If it fails again, contact the Reaxys Customer Care team.';

  let link = {
    url: 'https://service.elsevier.com/app/contact/supporthub/reaxys/',
    linkText: 'click here',
    // eslint-disable-next-line no-useless-concat
    preMessage: '\n' + 'If the problem persists,',
    postMessage: ' to contact the Reaxys Customer Care Team.\n',
  };

  const definedErrorMsg =
    'Something has gone wrong on our side. We are working on fixing this.\n' +
    '\n' +
    'In a few minutes please try your last action again. You could try refreshing the page or clearing your cache, which sometimes helps.\n';

  if (response.status) {
    // eslint-disable-next-line default-case
    switch (response.status) {
      case 400:
        title = '400 Bad Request Error';
        msg = 'The server was not able to process your last action, please try again.\n';
        break;
      case 429:
        title = '429 Too Many Requests';
        msg = 'You have reached the limit of concurrent requests. Please wait 2 minutes and try your last action again.\n';
        link = {
          url: 'https://service.elsevier.com/app/contact/supporthub/reaxys/',
          linkText: 'click here',
          // eslint-disable-next-line no-useless-concat
          preMessage: '\n' + 'If you believe that the limit is incorrectly applied, then please ',
          postMessage: ' to contact the Reaxys Customer Care Team.\n',
        };
        break;
      case 500:
        title = '500 Internal Server Error';
        msg =
          'Something has gone wrong on our side. We are working on fixing this.\n' +
          '\n' +
          'In a few minutes please try your last action again. You could try refreshing the page or clearing your cache, which sometimes helps.\n';
        break;
      case 502:
        title = '502 Bad Gateway Error';
        msg = definedErrorMsg;
        break;
      case 503:
        title = '503 Service Unavailable Error';
        msg = definedErrorMsg;
        break;
      case 504:
        title = '504 Timeout';
        msg = definedErrorMsg;
        break;
    }
  }
  return [title, msg, link];
}

// eslint-disable-next-line no-shadow
export function _openGenericErrorModal(response, isGoToLogin) {
  const level = getErrorLevel(response);
  let title = get(response, 'data.title', capitalize(level));
  let msg = '';
  let link;

  if (isString(response.data)) {
    const { headers } = response;
    const getHeader = headers?.get;
    const contentType =
      (typeof getHeader === 'function' && (getHeader.call(headers, 'content-type') || getHeader.call(headers, 'Content-Type'))) ||
      headers?.['Content-Type'] ||
      headers?.['content-type'];
    const lowerCaseContentType = isString(contentType) ? contentType.toLowerCase() : '';
    if (some(HTML_CONTENT_TYPES, (type) => includes(lowerCaseContentType, type))) {
      // if error response is string and content type HTML replace message with readable text
      [title, msg, link] = getGenericCDNErrorMessage(response, title);
    } else {
      msg = response.data;
    }
  } else if (isObject(response.data)) {
    const sm = get(response, 'data.message', '').replace(/;\d.*$/, '.');
    const lm = response.data.longMessage;
    const arrays = [sm !== lm && sm, sm.indexOf(lm) === -1 && lm, response.data.validationErrors];
    const flattenedArray = arrays.reduce((acc, val) => acc.concat(val), []);
    const compactArray = flattenedArray.filter(Boolean);
    msg = compactArray.join('\n ');
  } else {
    msg = 'Generic error';
  }

  trackResponseError(response, {
    message: msg,
  });

  return _showGenericErrorModal(response, isGoToLogin, {
    level,
    title,
    link,
    msg,
  });
}

// eslint-disable-next-line no-shadow
function _isMalformedResponse(response) {
  return isUndefined(response) || isUndefined(response.data) || isUndefined(response.status);
}

// eslint-disable-next-line no-shadow
function _storeHopintoPathIfNeeded(response, locationUrl) {
  // store hopinto only when error is related to unauthorized session
  if (locationUrl.indexOf(paths.HOP_INTO) >= 0 && response.status === 401) {
    AppStorage.instance.setToSessionStorage('hopintoPath', locationUrl);
  }
}

// eslint-disable-next-line no-shadow
function _isValidErrorStatus(response) {
  // In case when 404 error code is valid and means "resource not found"
  return get(response, 'config.validErrorStatusCodes', []).indexOf(response.status) !== -1;
}

// eslint-disable-next-line no-shadow
function _handleWebSocketTokenMismatch(response, info) {
  const connectUrl = RxSocket.getConnectUrl();
  const accessToken = Auth.getAccessToken();
  const isTokenMismatch = info && connectUrl && accessToken && connectUrl.indexOf(accessToken) === -1;
  const mismatchMessage = 'Session loss when got 401 via WS because of access tokens mismatch';

  if (isTokenMismatch) {
    // If 401 via WS and tokens don't match - do nothing
    trackResponseError(response, {
      message: mismatchMessage,
      errorNumber: 'tokenMismatch',
      component: 'WebSocket',
    });
    extend(response.data, { longMessage: mismatchMessage });
  } else {
    _openGenericErrorModal(response, true);
  }
}

// eslint-disable-next-line no-shadow
function _handle401or403(response, info, reqUrl) {
  if (response.data.type === 'CHOICE') {
    // only after login with departments
    _handleDataChoice(response);
    return Promise.reject(response);
  }

  if (response.data.type === loginLimitErrorType) {
    _handleActiveLoginsLimit(response);
    trackResponseError(response, {
      message: get(response, 'data.message') || 'Too many current active logins.',
      errorNumber: loginLimitErrorType,
    });
    return Promise.reject(response);
  }

  if (RequestUrls.userLogout.urlRegex.test(reqUrl)) {
    // if it's disconnect but session is already dead on BE, we ignore
    return Promise.reject(response);
  }

  _handleWebSocketTokenMismatch(response, info);
  return Promise.reject(response);
}

// eslint-disable-next-line no-shadow
function _handleResultNameSocketError(response) {
  trackResponseError(response, {
    message: get(response, 'message') || get(response, 'data.message') || unavailableResultsMessage,
    errorNumber: typeOfUnavailableResultsDialog,
  });
  // RX-958 - socket error related to invalid result name
  _handleUnavailableResults();
}

// eslint-disable-next-line no-shadow
function _handle429RateLimit(response) {
  // RX-2018 - socket error related to search and retrieval Rate Limit hit
  const level = getErrorLevel(response);
  const defaultTitle = get(response, 'data.title', capitalize(level));
  const [title, msg, link] = getGenericCDNErrorMessage(response, defaultTitle);

  _showGenericErrorModal(response, false, { level, title, msg, link });
  trackResponseError(response, { message: msg, component: 'WebSocket' });
}

/**
 * Handles all 400 status error cases.
 * Returns a rejected Promise if handled, or null to signal the caller should continue to remaining handlers.
 */
// eslint-disable-next-line no-shadow
function _handle400Error(response, info, reqUrl, errorNumber) {
  if (RequestUrls.changePassword.urlRegex.test(reqUrl)) {
    // change pass
    return Promise.reject(response);
  }
  if (errorNumber === 15 || errorNumber === 24) {
    // export: RXFE-2093: we have custom handler for export into export.controller
    // errorNumber 15 = export failed by xml server; 24 = restore failed because of job expired
    RXService.next(EXPORT_EVENTS.error, response, info);
    return Promise.reject(response);
  }
  if (errorNumber === 8) {
    // https://jira.elsevier.com/browse/RX-3979 if we got error from register form - do not show additional error modal
    return Promise.reject(response);
  }
  return null;
}

/**
 * Handles all non-401/403/400/404 error cases after the primary status checks.
 */
// eslint-disable-next-line no-shadow
function _handleRemainingErrors(response, info, status, errorNumber) {
  if (isResultNameSocketError(response)) {
    _handleResultNameSocketError(response);
    return Promise.reject(response);
  }
  if (status === 429 && errorNumber === 27) {
    _handle429RateLimit(response);
    return Promise.reject(response);
  }
  if (status === 500 && errorNumber === 12) {
    const requestId = get(info, 'requestId');
    if (Search.isSearchCancelled(requestId)) {
      Search.removeCancelledSearch(requestId);
      // eslint-disable-next-line consistent-return
      return;
    }
    _openGenericErrorModal(response);
    return Promise.reject(response);
  }
  if (!response.config || !response.config.ignoreErrorModal) {
    // any other error
    _openGenericErrorModal(response);
    return Promise.reject(response);
  }
  if (status === 400 && errorNumber === 5) {
    /**
     * RXFE-5002: HTTP response error has errorNumber 5 and status code 400, if we do not show error modal after
     * HTTP error response we will try to do it after callback from WS
     */
    _openGenericErrorModal(response);
  }
  return Promise.reject(response);
}

// eslint-disable-next-line no-shadow
function _handleStatusError(response, info, reqUrl) {
  const { status } = response;
  const errorNumber = get(response, 'data.errorNumber');

  if (status === 401 || status === 403) {
    return _handle401or403(response, info, reqUrl);
  }
  if (status === 404 || status === -1) {
    // loading result images, when server is slower than client and connection issues and missing CORS
    if (RequestUrls.userToken.url === reqUrl) {
      _openGenericErrorModal({ data: "It's Maintenance time. We'll be back soon, try again later." }, true);
    }
    return Promise.reject(response);
  }
  if (status === 400) {
    const handled = _handle400Error(response, info, reqUrl, errorNumber);
    if (handled !== null) return handled;
  }
  return _handleRemainingErrors(response, info, status, errorNumber);
}

// eslint-disable-next-line no-shadow
export function responseError(response, info) {
  if (get(response, 'xhrStatus') === 'abort') return Promise.reject(response);
  if (_isMalformedResponse(response)) return Promise.reject(response);
  if (response.data && response.data.hasUIHandler) return Promise.reject(response);

  const reqUrl = get(response, 'config.url', '');
  const locationUrl = getLocationHash().replace(/^#/, '');

  _storeHopintoPathIfNeeded(response, locationUrl);

  if (_isValidErrorStatus(response)) return Promise.reject(response);
  if (get(response, 'config.ignoreIntercepts')) return Promise.reject(response);

  const queryId = get(response, 'data.queryId');
  if (queryId) SocketBroker.stop(queryId);

  if (PROFILE_ERRORS_LIST.includes(get(response, 'data.errorNumber'))) {
    // custom handler in ProfileSettings factory
    return Promise.reject(response);
  }

  return _handleStatusError(response, info, reqUrl);
}

function isWebsocketRequest(config) {
  const url = get(config, 'url', '');
  if (isString(url)) {
    return url.indexOf(RxSocketUrls.ConnectionUrl) === -1;
  }
  return false;
}

function isIdleModalRequest(config) {
  const url = get(config, 'url', '');
  if (isString(url)) {
    return url.indexOf(idleModalUrl) !== -1;
  }
  return false;
}

function isModalRequest(config) {
  const url = get(config, 'url', '');
  if (isString(url)) {
    return url.indexOf(rxModalUrl) !== -1;
  }
  return false;
}

export function request(config) {
  if (isWebsocketRequest(config) && !isIdleModalRequest(config) && !isModalRequest(config)) {
    Auth.setLastActionTime(); // do not consider websocket reconnection as a user activity
  }
  const token = Auth.getAccessToken();

  const accessTokenAlreadyAttachedInRequestUrl = isString(config.url) && /[?&]access_token=/.test(config.url);

  // request to public service should not contain authorization header
  if (
    token &&
    !accessTokenAlreadyAttachedInRequestUrl &&
    config.Authorization !== false &&
    !config.ignoreAllSpecificHeaders &&
    config.url &&
    config.url.indexOf('/public/') < 0 &&
    config.url.indexOf('.html') < 0
  ) {
    // eslint-disable-next-line no-param-reassign
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Feature switching beta, only boolean true are taken
  const devOptions = Auth.getDevOptions();
  if (!isEmpty(devOptions)) {
    // eslint-disable-next-line no-param-reassign
    config.headers[CONSTANTS.CONTROL_HEADER] = devOptions;
  }
  extend(config.headers, generateTraceHeaders());

  return config;
}

export function response(data) {
  // probably getValueByNs(data, 'config.headers.Authorization', false)
  if (data && /^(f|ht)tps?:\/\//i.test(data.config.url) && isWebsocketRequest(data.config)) {
    Auth.setLastActionTime(); // do not consider websocket reconnection as a user activity
  }

  return data || Promise.resolve(data);
}
